import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Switch, FormControlLabel, MenuItem,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { insuranceDocumentTypeService } from '../../services/api';
import TopBar from '../../components/TopBar';

const emptyForm = { name: '', description: '', applicableTo: 'Both', sortOrder: 0, isActive: true };

const InsuranceDocumentTypesManagement = () => {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await insuranceDocumentTypeService.getAll();
      setTypes(res.data);
    } catch {
      setError('Failed to load document types');
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        description: item.description || '',
        applicableTo: item.applicableTo,
        sortOrder: item.sortOrder,
        isActive: item.isActive,
      });
    } else {
      setEditingItem(null);
      setFormData(emptyForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData(emptyForm);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await insuranceDocumentTypeService.update(editingItem.id, formData);
      } else {
        await insuranceDocumentTypeService.create(formData);
      }
      handleCloseDialog();
      loadData();
    } catch {
      setError('Failed to save document type');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document type?')) return;
    try {
      await insuranceDocumentTypeService.delete(id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete document type');
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'sortOrder', headerName: 'Order', width: 80 },
    { field: 'name', headerName: 'Document Name', flex: 1, minWidth: 220 },
    { field: 'description', headerName: 'Description', flex: 1.5, minWidth: 220, renderCell: (p) => p.value || '—' },
    { field: 'applicableTo', headerName: 'Applicable To', width: 130 },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 110,
      renderCell: (p) => (
        <Box sx={{
          px: 1.5, py: 0.3, borderRadius: 1, fontSize: '0.75rem', fontWeight: 600,
          bgcolor: p.value ? 'success.light' : 'error.light',
          color: p.value ? 'success.dark' : 'error.dark',
        }}>
          {p.value ? 'Active' : 'Inactive'}
        </Box>
      ),
    },
    {
      field: 'actions', headerName: 'Actions', width: 110, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" color="primary" onClick={() => handleOpenDialog(params.row)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Insurance Document Types" onBack={() => navigate('/dashboard')} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              Add Document Type
            </Button>
          </Box>
          <DataGrid
            rows={types}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25]}
            autoHeight
            disableSelectionOnClick
          />
        </Paper>
      </Container>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Document Type' : 'Add Document Type'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Document Name" value={formData.name} required autoFocus
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Pre-Authorization Form"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Description (optional)" value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline rows={2}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select fullWidth label="Applicable To" value={formData.applicableTo}
                  onChange={(e) => setFormData({ ...formData, applicableTo: e.target.value })}
                >
                  <MenuItem value="Cashless">Cashless</MenuItem>
                  <MenuItem value="Reimbursement">Reimbursement</MenuItem>
                  <MenuItem value="Both">Both</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth type="number" label="Sort Order" value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value, 10) || 0 })}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      color="success"
                    />
                  }
                  label={formData.isActive ? 'Active' : 'Inactive'}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">{editingItem ? 'Update' : 'Add'}</Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default InsuranceDocumentTypesManagement;
